import type { SVGProps } from "react";

/**
 * Editorial line-and-panel illustrations built from the same visual
 * vocabulary throughout: billboard frames, perspective road lines,
 * signage panels and urban silhouettes. Brand blue carries the
 * illustrations; red appears only as a single accent mark, matching
 * the logo's own restraint.
 */

export function HeroIllustration(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 620 520" fill="none" aria-hidden="true" {...props}>
      {/* perspective ground */}
      <path d="M0 470L620 470" stroke="var(--border-subtle)" strokeWidth="1.5" />
      <path d="M40 470L280 300" stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4 6" />
      <path d="M600 470L360 300" stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4 6" />
      <path d="M310 470L310 300" stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4 6" />

      {/* distant skyline */}
      <g opacity="0.55">
        <rect x="20" y="300" width="34" height="170" fill="var(--color-grey-100)" />
        <rect x="60" y="260" width="26" height="210" fill="var(--color-grey-100)" />
        <rect x="520" y="280" width="30" height="190" fill="var(--color-grey-100)" />
        <rect x="558" y="240" width="24" height="230" fill="var(--color-grey-100)" />
      </g>

      {/* main hoarding structure */}
      <g>
        <rect x="150" y="120" width="320" height="160" rx="3" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="2" />
        <rect x="164" y="134" width="292" height="132" fill="var(--color-blue-100)" />
        <line x1="164" y1="200" x2="456" y2="200" stroke="var(--color-blue-600)" strokeWidth="1" opacity="0.4" />
        <rect x="184" y="154" width="130" height="18" fill="var(--color-blue-600)" opacity="0.85" />
        <rect x="184" y="182" width="90" height="10" fill="var(--color-blue-600)" opacity="0.5" />
        <circle cx="432" cy="150" r="6" fill="var(--accent)" />
        {/* poles */}
        <line x1="220" y1="280" x2="212" y2="470" stroke="var(--text-primary)" strokeWidth="9" strokeLinecap="round" />
        <line x1="400" y1="280" x2="408" y2="470" stroke="var(--text-primary)" strokeWidth="9" strokeLinecap="round" />
        <line x1="170" y1="310" x2="220" y2="280" stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
        <line x1="450" y1="310" x2="400" y2="280" stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
      </g>

      {/* shop signage, left */}
      <g>
        <rect x="70" y="360" width="90" height="60" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
        <rect x="70" y="360" width="90" height="20" fill="var(--color-blue-600)" opacity="0.85" />
        <line x1="82" y1="392" x2="148" y2="392" stroke="var(--text-secondary)" strokeWidth="1.2" />
        <line x1="82" y1="404" x2="130" y2="404" stroke="var(--text-secondary)" strokeWidth="1.2" />
      </g>

      {/* building signage, right */}
      <g>
        <rect x="486" y="350" width="70" height="70" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
        <rect x="498" y="366" width="46" height="12" fill="var(--color-blue-600)" opacity="0.7" />
        <line x1="498" y1="392" x2="536" y2="392" stroke="var(--text-secondary)" strokeWidth="1.2" />
        <line x1="498" y1="402" x2="524" y2="402" stroke="var(--text-secondary)" strokeWidth="1.2" />
      </g>

      {/* directional sign */}
      <g>
        <line x1="330" y1="470" x2="330" y2="410" stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
        <path d="M330 400h44l10 9-10 9h-44z" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.4" />
        <line x1="338" y1="409" x2="368" y2="409" stroke="var(--text-secondary)" strokeWidth="1.2" />
      </g>

      {/* van silhouette */}
      <g>
        <rect x="220" y="432" width="70" height="28" rx="5" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
        <rect x="220" y="432" width="26" height="28" rx="5" fill="var(--color-blue-600)" opacity="0.8" />
        <circle cx="238" cy="462" r="6" fill="var(--text-primary)" />
        <circle cx="274" cy="462" r="6" fill="var(--text-primary)" />
      </g>
    </svg>
  );
}

export function HoardingMockup(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 480 420" fill="none" aria-hidden="true" {...props}>
      <line x1="0" y1="370" x2="480" y2="370" stroke="var(--border-subtle)" strokeWidth="1.5" />
      <path d="M0 370L200 250" stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4 6" />
      <path d="M480 370L280 250" stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4 6" />

      <rect x="60" y="50" width="360" height="200" rx="3" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="2" />
      <rect x="76" y="66" width="328" height="168" fill="var(--color-blue-100)" />
      <rect x="96" y="96" width="160" height="22" fill="var(--color-blue-600)" opacity="0.85" />
      <rect x="96" y="132" width="110" height="12" fill="var(--color-blue-600)" opacity="0.45" />
      <rect x="96" y="156" width="200" height="1.5" fill="var(--color-blue-600)" opacity="0.3" />
      <circle cx="368" cy="98" r="7" fill="var(--accent)" />

      <line x1="140" y1="250" x2="130" y2="370" stroke="var(--text-primary)" strokeWidth="10" strokeLinecap="round" />
      <line x1="340" y1="250" x2="350" y2="370" stroke="var(--text-primary)" strokeWidth="10" strokeLinecap="round" />
      <line x1="90" y1="290" x2="140" y2="250" stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
      <line x1="390" y1="290" x2="340" y2="250" stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function SignagePanelIllustration(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 480 420" fill="none" aria-hidden="true" {...props}>
      {/* shop fascia */}
      <rect x="30" y="40" width="200" height="70" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <rect x="30" y="40" width="200" height="26" fill="var(--color-blue-600)" opacity="0.85" />
      <line x1="46" y1="90" x2="180" y2="90" stroke="var(--text-secondary)" strokeWidth="1.2" />

      {/* directional board */}
      <path d="M270 60h130l16 14-16 14H270z" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="284" y1="74" x2="380" y2="74" stroke="var(--text-secondary)" strokeWidth="1.2" />

      {/* reception sign */}
      <rect x="30" y="160" width="160" height="46" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.4" />
      <circle cx="48" cy="183" r="3" fill="var(--accent)" />
      <line x1="60" y1="176" x2="170" y2="176" stroke="var(--text-secondary)" strokeWidth="1.1" />
      <line x1="60" y1="190" x2="140" y2="190" stroke="var(--text-secondary)" strokeWidth="1.1" />

      {/* glow letters */}
      <g stroke="var(--color-blue-600)" strokeWidth="6" strokeLinecap="round">
        <line x1="230" y1="180" x2="230" y2="206" />
        <line x1="248" y1="180" x2="248" y2="206" />
        <path d="M266 180v26M266 180l14 26M280 180v-26" />
        <line x1="300" y1="180" x2="300" y2="206" />
      </g>

      {/* banner */}
      <rect x="30" y="250" width="210" height="120" fill="var(--color-blue-100)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <line x1="30" y1="250" x2="10" y2="240" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="240" y1="250" x2="260" y2="240" stroke="var(--text-primary)" strokeWidth="1.4" />
      <rect x="48" y="272" width="120" height="14" fill="var(--color-blue-600)" opacity="0.7" />
      <rect x="48" y="298" width="160" height="8" fill="var(--color-blue-600)" opacity="0.4" />

      {/* office branding wall */}
      <rect x="270" y="250" width="180" height="120" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <line x1="290" y1="280" x2="430" y2="280" stroke="var(--color-blue-600)" strokeWidth="3" opacity="0.6" />
      <line x1="290" y1="300" x2="400" y2="300" stroke="var(--text-secondary)" strokeWidth="1.2" />
      <line x1="290" y1="316" x2="380" y2="316" stroke="var(--text-secondary)" strokeWidth="1.2" />
    </svg>
  );
}

export function ShowcaseBillboard(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <rect x="20" y="30" width="240" height="110" fill="var(--color-blue-100)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <rect x="40" y="55" width="120" height="16" fill="var(--color-blue-600)" opacity="0.85" />
      <rect x="40" y="80" width="80" height="9" fill="var(--color-blue-600)" opacity="0.45" />
      <circle cx="222" cy="58" r="5" fill="var(--accent)" />
      <line x1="70" y1="140" x2="65" y2="180" stroke="var(--text-primary)" strokeWidth="7" strokeLinecap="round" />
      <line x1="210" y1="140" x2="215" y2="180" stroke="var(--text-primary)" strokeWidth="7" strokeLinecap="round" />
    </svg>
  );
}

export function ShowcaseShopfront(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <rect x="30" y="40" width="220" height="130" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <rect x="30" y="40" width="220" height="34" fill="var(--color-blue-600)" opacity="0.85" />
      <rect x="55" y="95" width="70" height="75" fill="var(--color-blue-100)" />
      <rect x="150" y="95" width="70" height="75" fill="var(--color-blue-100)" />
    </svg>
  );
}

export function ShowcaseBuilding(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <rect x="60" y="20" width="160" height="160" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <rect x="88" y="50" width="104" height="20" fill="var(--color-blue-600)" opacity="0.75" />
      {Array.from({ length: 4 }).map((_, row) =>
        Array.from({ length: 4 }).map((_, col) => (
          <rect
            key={`${row}-${col}`}
            x={88 + col * 28}
            y={100 + row * 18}
            width="16"
            height="10"
            fill="var(--color-blue-100)"
          />
        ))
      )}
    </svg>
  );
}

export function ShowcaseBanner(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <rect x="30" y="30" width="220" height="140" fill="var(--color-blue-100)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <line x1="30" y1="30" x2="10" y2="16" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="250" y1="30" x2="270" y2="16" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="30" y1="170" x2="10" y2="184" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="250" y1="170" x2="270" y2="184" stroke="var(--text-primary)" strokeWidth="1.4" />
      <rect x="55" y="65" width="150" height="18" fill="var(--color-blue-600)" opacity="0.8" />
      <rect x="55" y="95" width="100" height="10" fill="var(--color-blue-600)" opacity="0.4" />
    </svg>
  );
}

export function ShowcaseIndoor(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <rect x="20" y="20" width="240" height="160" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.6" />
      <line x1="20" y1="70" x2="260" y2="70" stroke="var(--text-secondary)" strokeWidth="1" opacity="0.5" />
      <rect x="60" y="30" width="160" height="24" fill="var(--color-blue-600)" opacity="0.7" />
      <line x1="45" y1="110" x2="235" y2="110" stroke="var(--text-secondary)" strokeWidth="1.2" />
      <line x1="45" y1="130" x2="200" y2="130" stroke="var(--text-secondary)" strokeWidth="1.2" />
      <line x1="45" y1="150" x2="220" y2="150" stroke="var(--text-secondary)" strokeWidth="1.2" />
    </svg>
  );
}

export function ShowcaseDirectional(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 280 200" fill="none" aria-hidden="true" {...props}>
      <line x1="140" y1="180" x2="140" y2="70" stroke="var(--text-primary)" strokeWidth="6" strokeLinecap="round" />
      <path d="M140 40h90l14 15-14 15h-90z" fill="var(--bg-surface)" stroke="var(--text-primary)" strokeWidth="1.4" />
      <path d="M60 90h80l14 15-14 15H60z" fill="var(--color-blue-100)" stroke="var(--text-primary)" strokeWidth="1.4" />
      <line x1="156" y1="55" x2="210" y2="55" stroke="var(--text-secondary)" strokeWidth="1.2" />
      <line x1="76" y1="105" x2="120" y2="105" stroke="var(--text-secondary)" strokeWidth="1.2" />
    </svg>
  );
}
